import React from 'react';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import Typography from '@mui/material/Typography';
import { useHistory, useLocation } from 'react-router-dom';
import { useAppContext } from '../../libs/appContextLib';
import { getVisibleRoutes } from '../../libs/navigationLogic';
import { routeRegistry } from '../../navigation/routeRegistry';
import { getCachedCognitoUser } from '../../utils/cognitoSessionCache';
import { getShortcutDisplay, useKeyboardShortcuts } from '../../hooks/useKeyboardShortcuts';
import DatasetDiscovery from './DatasetDiscovery';

export function ColdStartShortcutHint({ visible }) {
  const shortcutLabel = React.useMemo(() => getShortcutDisplay('search'), []);

  if (!visible) return null;

  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.75,
        px: 1.25,
        py: 0.75,
        mt: 0.5,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: '8px',
        backgroundColor: 'background.paper',
        color: 'text.secondary',
      }}
    >
      <Typography variant="caption">Press</Typography>
      <Box
        component="kbd"
        sx={{
          px: 0.75,
          py: 0.25,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: '4px',
          backgroundColor: 'grey.50',
          color: 'text.primary',
          fontFamily: 'inherit',
          fontSize: '0.75rem',
          fontWeight: 600,
          lineHeight: 1.3,
          boxShadow: '0 1px 1px rgba(39, 41, 49, 0.08)',
        }}
      >
        {shortcutLabel}
      </Box>
      <Typography variant="caption">to jump to a destination while QFlow loads</Typography>
    </Box>
  );
}

/**
 * Keeps global navigation usable while App is validating a returning user's
 * session and loading the initial account data. It intentionally uses the
 * static registry, so opening it never waits for an authenticated API request.
 */
export default function ColdStartCommandPalette({
  isAuthenticating,
  isAuthenticated,
  isLoggingOut,
  hasStoredSession,
  open,
  onOpen,
  onClose,
}) {
  const history = useHistory();
  const location = useLocation();
  const { currUser, dataStatus } = useAppContext();
  const cachedUser = React.useMemo(() => getCachedCognitoUser(), []);
  const effectiveUser = currUser?.role ? currUser : cachedUser;
  const shortcutEnabled = isAuthenticating && !isLoggingOut && hasStoredSession;

  useKeyboardShortcuts({
    onSearchFocus: onOpen,
    onEscape: onClose,
    enabled: shortcutEnabled,
  });

  React.useEffect(() => {
    if (!isAuthenticating && !isAuthenticated && open) {
      onClose();
    }
  }, [isAuthenticating, isAuthenticated, open, onClose]);

  const visibleRoutes = React.useMemo(() => {
    if (!effectiveUser?.role) return [];

    return getVisibleRoutes(
      effectiveUser,
      dataStatus?.routePreferences || {},
      {},
      routeRegistry
    ).filter((route) => !route.isPermanent);
  }, [effectiveUser, dataStatus?.routePreferences]);

  const handleNavigate = React.useCallback((path, _route, event) => {
    if (!path) return;

    if (event?.metaKey || event?.ctrlKey) {
      window.open(path, '_blank', 'noopener,noreferrer');
    } else {
      history.push(path);
    }
  }, [history]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      aria-labelledby="cold-start-command-palette-title"
      PaperProps={{
        sx: {
          alignSelf: 'flex-start',
          mt: { xs: 4, sm: 8 },
          borderRadius: '8px',
          overflow: 'hidden',
          boxShadow: '0 24px 64px rgba(39, 41, 49, 0.22)',
        },
      }}
    >
      <Typography
        id="cold-start-command-palette-title"
        variant="subtitle2"
        sx={{ px: 2, pt: 2, color: 'text.secondary' }}
      >
        Go to a destination
      </Typography>

      {visibleRoutes.length > 0 ? (
        <Box sx={{ pt: 0.5, pb: 0.5 }}>
          <DatasetDiscovery
            datasets={visibleRoutes}
            searchDatasets={visibleRoutes}
            onNavigate={handleNavigate}
            currentPath={location.pathname}
            pinnedDatasetIds={visibleRoutes.map((route) => route.id)}
            defaultExpanded
            enableSearchPreview={false}
            onNavigateComplete={onClose}
            searchPlaceholder="Type a driver, dataset, account, or deal name..."
          />
        </Box>
      ) : (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 3 }}>
          <CircularProgress size={20} />
          <Typography variant="body2" color="text.secondary">
            Loading your destinations…
          </Typography>
        </Box>
      )}
    </Dialog>
  );
}
